import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftSnackbar from "components/SoftSnackbar";
import SoftButton from "components/SoftButton";
import DataTable from "components/QuestDataTable";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { API_URL } from "config";
import { useAuth } from "../../../AuthContext";
import { useModulePermissions } from "utils/modulePermissions";
import {
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";

const DesignationList = () => {
  const navigate = useNavigate();
  const { isAuthReady, permissions = [], userPermission = [] } = useAuth();
  const { hasModulePermission, canViewField } = useModulePermissions(
    permissions,
    userPermission,
    "designation"
  );

  const [designations, setDesignations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", color: "success" });
  const [deletingId, setDeletingId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDesignation, setSelectedDesignation] = useState(null);

  const canViewPage = hasModulePermission("designation.pages.list.view");
  const canCreate =
    hasModulePermission("designation.pages.create.view") &&
    hasModulePermission("designation.actions.open_create");
  const canOpenTrashed =
    hasModulePermission("designation.pages.trashed.view") &&
    hasModulePermission("designation.actions.open_trashed");
  const canOpenEdit =
    hasModulePermission("designation.pages.edit.view") &&
    hasModulePermission("designation.actions.open_edit");
  const canDelete = hasModulePermission("designation.actions.delete");
  const canConfirmDelete = hasModulePermission("designation.actions.confirm_delete");
  const canCancelDelete = hasModulePermission("designation.actions.cancel_delete");

  const closeSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

  const fetchDesignations = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${API_URL}/designations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Failed to fetch designations");
      }
      const { data } = await res.json();
      setDesignations(data || []);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || "Error loading designations",
        color: "error",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthReady) return;
    if (!canViewPage) {
      setLoading(false);
      return;
    }
    fetchDesignations();
  }, [canViewPage, fetchDesignations, isAuthReady]);

  const handleDeleteClick = (designation) => {
    if (!canDelete) {
      setSnackbar({
        open: true,
        message: "You do not have permission to delete designations.",
        color: "error",
      });
      return;
    }
    setSelectedDesignation(designation);
    setDeleteDialogOpen(true);
  };

  const handleEditClick = (designation) => {
    if (!canOpenEdit) {
      setSnackbar({
        open: true,
        message: "You do not have permission to edit designations.",
        color: "error",
      });
      return;
    }
    navigate(`/designations/edit/${designation.id}`);
  };

  const handleDeleteConfirm = async () => {
    if (!canDelete || !canConfirmDelete) {
      setSnackbar({
        open: true,
        message: "You do not have permission to confirm delete.",
        color: "error",
      });
      return;
    }

    if (!selectedDesignation) return;

    try {
      setDeletingId(selectedDesignation.id);
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${API_URL}/designations/${selectedDesignation.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Delete failed");
      }
      const data = await res.json();
      setSnackbar({
        open: true,
        message: data.message || "Designation deleted successfully",
        color: "success",
      });
      fetchDesignations();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || "Delete failed",
        color: "error",
      });
    } finally {
      setDeletingId(null);
      setDeleteDialogOpen(false);
      setSelectedDesignation(null);
    }
  };

  const closeDeleteDialog = () => {
    if (!canCancelDelete || deletingId) return;
    setDeleteDialogOpen(false);
    setSelectedDesignation(null);
  };

  const columns = useMemo(() => {
    const visibleColumns = [];
    if (canViewField("id")) visibleColumns.push({ Header: "ID", accessor: "id", align: "left" });
    if (canViewField("name")) visibleColumns.push({ Header: "Name", accessor: "name", align: "left" });
    if (canViewField("created_at")) {
      visibleColumns.push({ Header: "Created At", accessor: "created_at", align: "left" });
    }
    if (canViewField("status")) visibleColumns.push({ Header: "Status", accessor: "status", align: "center" });
    if (canViewField("actions") && (canOpenEdit || canDelete)) {
      visibleColumns.push({ Header: "Actions", accessor: "actions", align: "center", disableSortBy: true });
    }
    return visibleColumns;
  }, [canDelete, canOpenEdit, canViewField]);

  const rows = useMemo(
    () =>
      designations.map((desg) => ({
        id: desg.id,
        name: desg.name,
        created_at: desg.created_at ? new Date(desg.created_at).toLocaleString() : "-",
        status: (
          <SoftBox display="inline-flex" alignItems="center" px={1.5} py={0.5} borderRadius={1}>
            <SoftTypography variant="caption" color={desg.status ? "info" : "error"}>
              {desg.status ? "ACTIVE" : "INACTIVE"}
            </SoftTypography>
          </SoftBox>
        ),
        actions: (
          <SoftBox display="flex" justifyContent="center">
            {canOpenEdit && (
              <Tooltip title="Edit Designation">
                <IconButton size="small" onClick={() => handleEditClick(desg)} aria-label="edit">
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title="Delete Designation">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleDeleteClick(desg)}
                  aria-label="delete"
                  disabled={deletingId === desg.id}
                >
                  {deletingId === desg.id ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <DeleteIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            )}
          </SoftBox>
        ),
      })),
    [canDelete, canOpenEdit, deletingId, designations]
  );

  if (!isAuthReady || (canViewPage && loading)) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox display="flex" justifyContent="center" alignItems="center" height="200px">
          <CircularProgress size={50} color="info" />
        </SoftBox>
      </DashboardLayout>
    );
  }

  if (!canViewPage) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox p={3}>
          <SoftTypography variant="h5" fontWeight="bold">
            Designation List
          </SoftTypography>
          <SoftTypography variant="body2" color="text" mt={1}>
            You do not have permission to view designations.
          </SoftTypography>
        </SoftBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <SoftSnackbar
        color={snackbar.color}
        icon="notifications"
        title="Designation Management"
        content={snackbar.message}
        open={snackbar.open}
        close={closeSnackbar}
        onClose={closeSnackbar}
        time={3000}
      />

      <SoftBox p={3}>
        <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <SoftTypography variant="h4" fontWeight="bold">
            Designation List
          </SoftTypography>
          <SoftBox display="flex" gap={1} flexWrap="wrap" justifyContent="flex-end">
            {canOpenTrashed && (
              <SoftButton
                size="small"
                color="warning"
                onClick={() => navigate("/designations/trashed")}
                startIcon={<Icon>delete</Icon>}
              >
                Trash
              </SoftButton>
            )}
            {canCreate && (
              <SoftButton
                size="small"
                color="info"
                startIcon={<Icon>add</Icon>}
                onClick={() => navigate("/designations/add")}
              >
                Add New Designation
              </SoftButton>
            )}
          </SoftBox>
        </SoftBox>

        <Card>
          {columns.length > 0 ? (
            <DataTable table={{ columns, rows }} isSorted emptyDataMessage="No designations found." />
          ) : (
            <SoftBox p={3}>
              <SoftTypography variant="body2" color="text">
                No visible fields are assigned for this page.
              </SoftTypography>
            </SoftBox>
          )}
        </Card>
      </SoftBox>

      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <SoftTypography variant="body2">
            Are you sure you want to delete the designation{" "}
            <strong>{selectedDesignation?.name}</strong>?
          </SoftTypography>
        </DialogContent>
        <DialogActions>
          {canConfirmDelete && (
            <SoftButton
              color="info"
              variant="contained"
              onClick={handleDeleteConfirm}
              disabled={deletingId === selectedDesignation?.id}
              sx={{
                backgroundColor: "rgb(23, 193, 232)",
                "&:hover": { backgroundColor: "rgba(23, 193, 232, 0.8)" },
                color: "#fff",
                minWidth: 100,
              }}
            >
              {deletingId === selectedDesignation?.id ? "Processing..." : "Confirm"}
            </SoftButton>
          )}
          {canCancelDelete && (
            <SoftButton
              variant="contained"
              onClick={closeDeleteDialog}
              disabled={Boolean(deletingId)}
              sx={{
                backgroundColor: "red",
                "&:hover": { backgroundColor: "darkred" },
                color: "#fff",
                minWidth: 100,
              }}
            >
              Cancel
            </SoftButton>
          )}
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default DesignationList;
